import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api';
import { orgNavigationKeys } from '@/hooks/api/useNavigationConfig';
import type { RoleNavigationPermission } from '@/types/organizations';

export interface RolePermissionPayload {
  role: 'admin' | 'member' | 'coach' | 'player';
  navigation_path: string;
  has_access: boolean;
  created_by?: string;
}

const ROLE_PERMISSIONS_STALE_TIME = 5 * 60_000;

export const rolePermissionsKeys = {
  all: ['role-navigation-permissions'] as const,
  byOrg: (orgId: string) => [...rolePermissionsKeys.all, orgId] as const,
};

export const listOrganizationRolePermissions = (orgId: string) =>
  apiGet<RoleNavigationPermission[]>(`/organizations/${orgId}/navigation-permissions`);

export const createOrganizationRolePermission = (orgId: string, payload: RolePermissionPayload) =>
  apiPost<RoleNavigationPermission>(`/organizations/${orgId}/navigation-permissions`, payload);

export const updateOrganizationRolePermission = (
  orgId: string,
  permissionId: string,
  payload: RolePermissionPayload,
) =>
  apiPut<RoleNavigationPermission>(
    `/organizations/${orgId}/navigation-permissions/${permissionId}`,
    payload,
  );

export const deleteOrganizationRolePermission = (orgId: string, permissionId: string) =>
  apiDelete<{ deleted: boolean }>(`/organizations/${orgId}/navigation-permissions/${permissionId}`);

export const useRolePermissionsQuery = (orgId?: string, enabled = true) =>
  useQuery({
    queryKey: rolePermissionsKeys.byOrg(orgId || 'none'),
    queryFn: () => listOrganizationRolePermissions(orgId as string),
    enabled: !!orgId && enabled,
    staleTime: ROLE_PERMISSIONS_STALE_TIME,
  });

export const useRolePermissionsMutations = (orgId?: string) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    if (orgId) {
      void queryClient.invalidateQueries({
        queryKey: rolePermissionsKeys.byOrg(orgId),
      });
      void queryClient.invalidateQueries({
        queryKey: orgNavigationKeys.byOrg(orgId),
      });
    }
  };

  const upsertPermissionMutation = useMutation({
    mutationFn: async ({
      role,
      navigationPath,
      hasAccess,
      userId,
    }: {
      role: 'admin' | 'member' | 'coach' | 'player';
      navigationPath: string;
      hasAccess: boolean;
      userId: string;
    }) => {
      if (!orgId) throw new Error('Organización no disponible');

      const currentPermissions = await listOrganizationRolePermissions(orgId);
      const existing = currentPermissions.find(
        (permission) => permission.role === role && permission.navigation_path === navigationPath,
      );

      const payload: RolePermissionPayload = {
        role,
        navigation_path: navigationPath,
        has_access: hasAccess,
        created_by: userId,
      };

      if (existing) {
        return updateOrganizationRolePermission(orgId, existing.id, payload);
      }

      return createOrganizationRolePermission(orgId, payload);
    },
    onSuccess: invalidate,
  });

  const updateMultiplePermissionsMutation = useMutation({
    mutationFn: async ({
      permissions,
      userId,
    }: {
      permissions: Array<{
        role: 'admin' | 'member' | 'coach' | 'player';
        navigationPath: string;
        hasAccess: boolean;
      }>;
      userId: string;
    }) => {
      if (!orgId) throw new Error('Organización no disponible');

      const currentPermissions = await listOrganizationRolePermissions(orgId);

      return Promise.all(
        permissions.map(async (permission) => {
          const existing = currentPermissions.find(
            (item) =>
              item.role === permission.role && item.navigation_path === permission.navigationPath,
          );

          const payload: RolePermissionPayload = {
            role: permission.role,
            navigation_path: permission.navigationPath,
            has_access: permission.hasAccess,
            created_by: userId,
          };

          if (existing) {
            return updateOrganizationRolePermission(orgId, existing.id, payload);
          }

          return createOrganizationRolePermission(orgId, payload);
        }),
      );
    },
    onSuccess: invalidate,
  });

  const resetPermissionsMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('Organización no disponible');
      const permissions = await listOrganizationRolePermissions(orgId);
      await Promise.all(
        permissions.map((permission) => deleteOrganizationRolePermission(orgId, permission.id)),
      );
    },
    onSuccess: invalidate,
  });

  const createPermissionMutation = useMutation({
    mutationFn: (payload: RolePermissionPayload) =>
      createOrganizationRolePermission(orgId as string, payload),
    onSuccess: invalidate,
  });

  const updatePermissionMutation = useMutation({
    mutationFn: ({
      permissionId,
      payload,
    }: {
      permissionId: string;
      payload: RolePermissionPayload;
    }) => updateOrganizationRolePermission(orgId as string, permissionId, payload),
    onSuccess: invalidate,
  });

  const deletePermissionMutation = useMutation({
    mutationFn: (permissionId: string) =>
      deleteOrganizationRolePermission(orgId as string, permissionId),
    onSuccess: invalidate,
  });

  return {
    upsertPermissionMutation,
    updateMultiplePermissionsMutation,
    resetPermissionsMutation,
    createPermissionMutation,
    updatePermissionMutation,
    deletePermissionMutation,
  };
};
